<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Способы доставки</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-popast"
            class="typography__headline2"
          >
            Как попасть
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>Давайте добавим способы доставки вашего проекта.</p>

            <p>
              В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Данные проекта&raquo;. На&nbsp;появившейся странице
              выберите &laquo;Доставка&raquo;.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/sposoby-dostavki-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="dobavleniye-sposobov-dostavki"
            class="typography__headline2"
          >
            Добавление способов доставки
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>На&nbsp;странице можно настроить минимальную сумму заказа. По-умолчанию она равна нулю.</p>

            <p>
              После изменений нажмите &laquo;Сохранить&raquo;. После успешного сохранения увидите в&nbsp;правом верхнем
              углу уведомление.
            </p>

            <p>По&nbsp;кнопке сверху справа добавим способ доставки.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/sposoby-dostavki-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>В&nbsp;описании способа доставки можно указать важную дополнительную информацию.</p>

            <p>
              Переключатель с&nbsp;запросом адреса доставки влияет на&nbsp;возможность покупки ваших товаров без
              указания адреса доставки на&nbsp;оформлении. Например, если вы&nbsp;продаете цифровые товары или хотите
              добавить самовывоз.
            </p>

            <p>Как заполните всю нужную информацию, нажмите &laquo;Добавить&raquo; в&nbsp;самом низу окна.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/sposoby-dostavki-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>
              После успешного добавления увидите в&nbsp;правом верхнем углу уведомление и&nbsp;внизу страницы новый
              способ доставки.
            </p>

            <p>
              Здесь можно посмотреть всю его информацию, быстро скрыть его с&nbsp;сайта, перейти к&nbsp;редактированию,
              дублировать или удалить.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/sposoby-dostavki-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            Со способами доставки разобрались. Самое время перейти к заполнению
            <BaseLink
              class="ui-link-secondary"
              to="/faq/articles/kategorii-tovarov"
              >категорий товаров</BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Заказы с товарами",
    url: "/faq/categories/zakazy-s-tovarami",
  },
  {
    name: "Способы доставки",
  },
];

const menu = [
  {
    name: "Как&nbsp;попасть",
    url: "kak-popast",
  },
  {
    name: "Добавление&nbsp;способов&nbsp;доставки",
    url: "dobavleniye-sposobov-dostavki",
  },
];

const relatedArticles = [
  {
    name: "Категории товаров",
    slug: "kategorii-tovarov",
  },
  {
    name: "Добавление и&nbsp;редактирование товара",
    slug: "dobavlenie-i-redaktirovanie-tovara",
  },
  {
    name: "Список товаров и&nbsp;их данные",
    slug: "spisok-tovarov-i-ikh-dannye",
  },
  {
    name: "Цифровые товары",
    slug: "tsifrovye-tovary",
  },
  {
    name: "Список заказов и&nbsp;их данные",
    slug: "spisok-zakazov-i-ikh-dannye",
  },
  {
    name: "Добавление и&nbsp;редактирование заказа",
    slug: "dobavlenie-i-redaktirovanie-zakaza",
  },
];

useSeoMeta({
  title: "Способы доставки",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
